import {
  type AddToCartSectionInfoFragment,
  AddToCartType,
  type ItemLineOptionsInput,
  useGetAddToCartTypeQuery
} from '@stuller/stullercom/data-access/apollo-queries'
import { type ReactNode, useState, type ReactElement, useEffect, useMemo, useCallback } from 'react'
import { type AddToCartOptions, AddToCartSectionContext } from './AddToCartSectionContext'
import { useAuth } from '@stuller/stullercom/feat/auth'
import { type ItemLineOptions } from './types'
import { type TrackingQueryStringParams } from '@stuller/stullercom/feat/google-tag-manager'

interface AddToCartSectionContextProviderProps {
  /**
   * Most of the data needed to produce the entire add to cart section
   */
  addToCartSectionData: AddToCartSectionInfoFragment
  /**
   * Boolean to determine if the add to cart section data is loading
   */
  addToCartSectionDataLoading?: boolean
  /**
   * Callback to notify parent component when the add to cart data has changed
   */
  onOptionsChanged: (options: Partial<ItemLineOptionsInput>) => void
  /**
   * All tracking fields that can come in on the querystring from pages rendering the AddToCartSection
   */
  trackingParams?: TrackingQueryStringParams
  /**
   * Children to render inside the context provider
   */
  children: ReactNode
}

/**
 * Maps the add to cart options with defaults
 */
function mapAddToCartOptions (options: Partial<ItemLineOptions>): AddToCartOptions {
  return {
    quantity: options?.quantity ?? 1,
    quantityInput: options?.quantity?.toString() ?? '1',
    isAnnealed: options?.isAnnealed ?? false,
    isForConsignment: options?.isForConsignment ?? false,
    isMatched: options?.isMatched ?? false,
    specialInstructions: options?.specialInstructions ?? ''
  }
}

/**
 * The context provider for the AddToCartSection to help manage the state of the add to cart options
 */
function AddToCartSectionContextProvider ({
  addToCartSectionData,
  addToCartSectionDataLoading = false,
  onOptionsChanged,
  trackingParams,
  children
}: AddToCartSectionContextProviderProps): ReactElement {
  const { authUser } = useAuth()
  const { data: addToCartTypeData } = useGetAddToCartTypeQuery()
  const addToCartType = addToCartTypeData?.viewerPermissions?.addToCartType ?? AddToCartType.Hidden
  const options: Partial<ItemLineOptions> = useMemo(
    () => addToCartSectionData.options ?? { quantity: 1 },
    [addToCartSectionData.options]
  )
  const {
    itemId: dataItemId,
    inStockStatus,
    nonConsignmentPrice,
    shippingCountdown
  } = addToCartSectionData
  const [addToCartOptions, setAddToCartOptions] = useState<AddToCartOptions>(mapAddToCartOptions(options))

  const hasScheduledOrder = authUser?.user?.scheduledOrder != null
  const itemPrice = nonConsignmentPrice?.extended.total.value ?? 0
  const itemId = dataItemId.id
  const itemType = dataItemId.type
  let isDiamond = false
  let isGemstone = false
  let isLabGrown = false
  if (addToCartSectionData.__typename === 'SerializedProduct') {
    isDiamond = addToCartSectionData.isDiamond
    isGemstone = addToCartSectionData.isGemstone
    isLabGrown = addToCartSectionData.isLabGrown
  }

  const setAddToCartOptionsWrapper = useCallback((newOptions: Partial<AddToCartOptions>) => {
    if (addToCartSectionDataLoading) {
      return
    }
    setAddToCartOptions((prevOptions) => ({
      ...prevOptions,
      ...newOptions
    }))
  }, [setAddToCartOptions, addToCartSectionDataLoading])

  const onOptionsChangedWrapper = useCallback((newOptions: Partial<ItemLineOptionsInput>) => {
    if (addToCartSectionDataLoading) {
      return
    }
    onOptionsChanged(newOptions)
  }, [onOptionsChanged, addToCartSectionDataLoading])

  useEffect(() => {
    setAddToCartOptions((prevOptions) => ({
      ...prevOptions,
      quantity: options?.quantity ?? 1,
      isAnnealed: options?.isAnnealed ?? false,
      isForConsignment: options?.isForConsignment ?? false,
      isMatched: options?.isMatched ?? false,
      specialInstructions: options?.specialInstructions ?? ''
    }))
  }, [setAddToCartOptions, options])

  return (
    <AddToCartSectionContext.Provider
      value={{
        itemId,
        itemType,
        isDiamond,
        isGemstone,
        isLabGrown,
        itemPrice,
        hasScheduledOrder,
        quantityOnHand: inStockStatus.backOrderInfo.quantityOnHand,
        onAddToCartDataChanged: onOptionsChangedWrapper,
        cutoffDate: shippingCountdown?.cutoffDate,
        countdownKind: shippingCountdown?.countdownKind,
        options,
        trackingParams,
        addToCartType,
        addToCartOptions,
        setAddToCartOptions: setAddToCartOptionsWrapper
      }}
    >
      {children}
    </AddToCartSectionContext.Provider>
  )
}

export {
  AddToCartSectionContextProvider
}
